import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Table,
    TableHead,
    TableBody,
    TableCell,
    TableRow,
    TableContainer,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Snackbar,
    Menu,
    MenuItem,
    IconButton,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { MoreVert } from "@mui/icons-material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { deleteProductTab, getProductTabsByProductId } from "../../Apis/ProductTabsApi";

const GetProductTabs = () => {
    const { productId } = useParams();
    const [tabs, setTabs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openConfirm, setOpenConfirm] = useState(false);
    const [selectedTab, setSelectedTab] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [anchorEl, setAnchorEl] = useState(null);

    const navigate = useNavigate();

    useEffect(() => {
        fetchTabs();
    }, [productId]);

    const fetchTabs = async () => {
        try {
            setLoading(true);
            const data = await getProductTabsByProductId(productId);
            setTabs(data?.data || []);
            setError(null);
        } catch {
            setError("Failed to fetch product tabs.");
        } finally {
            setLoading(false);
        }
    };

    const handleMenuOpen = (event, tab) => {
        setAnchorEl(event.currentTarget);
        setSelectedTab(tab);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedTab(null);
    };

    const handleEdit = () => {
        navigate(`/getproducttabs/edit/${selectedTab._id || selectedTab.id}?productId=${productId}`);
        handleMenuClose();
    };

    const handleDeleteClick = () => {
        setOpenConfirm(true);
        handleMenuClose();
    };

    const handleDeleteConfirm = async () => {
        try {
            await deleteProductTab(selectedTab._id || selectedTab.id);
            setTabs((prev) => prev.filter((t) => (t._id || t.id) !== (selectedTab._id || selectedTab.id)));
            setSnackbar({ open: true, message: "Tab deleted successfully!", severity: "success" });
        } catch {
            setSnackbar({ open: true, message: "Failed to delete tab", severity: "error" });
        } finally {
            setOpenConfirm(false);
            setSelectedTab(null);
        }
    };

    return (
        <Box sx={{ padding: 3, width: "95%" }}>
            {/* Header */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                <Typography variant="h5" fontWeight="bold">Product Tabs</Typography>
                <Button
                    variant="contained"
                    onClick={() => navigate(`/getproducttabs/add?productId=${productId}`)}
                >
                    + Add Tab
                </Button>
            </Box>

            {/* Loader */}
            {loading && (
                <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                    <CircularProgress />
                </Box>
            )}

            {/* Error */}
            {error && <MuiAlert severity="error">{error}</MuiAlert>}

            {/* Tabs Table */}
            {!loading && (
                <TableContainer component={Paper} elevation={3}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: "bold" }}>Image</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>Title</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>Content</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tabs.length ? (
                                tabs.map((tab) => (
                                    <TableRow key={tab._id || tab.id}>
                                        <TableCell>
                                            <Box
                                                component="img"
                                                src={tab.tabImage || "/placeholder.png"}
                                                alt={tab.tabTitle}
                                                sx={{ width: 70, height: 70, borderRadius: 2, objectFit: "cover" }}
                                            />
                                        </TableCell>
                                        <TableCell>{tab.tabTitle}</TableCell>
                                        <TableCell sx={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            {tab.tabContent.replace(/<[^>]+>/g, "")}
                                        </TableCell>
                                        <TableCell>
                                            <IconButton onClick={(e) => handleMenuOpen(e, tab)}>
                                                <MoreVert />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                        No tabs found for this product.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>

                    </Table>
                </TableContainer>
            )}

            {/* Action Menu */}
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItem onClick={handleEdit}>Edit</MenuItem>
                <MenuItem onClick={handleDeleteClick} sx={{ color: "error.main" }}>Delete</MenuItem>
            </Menu>

            {/* Delete Confirmation Dialog */}
            <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this tab? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenConfirm(false)}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">Delete</Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <MuiAlert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </MuiAlert>
            </Snackbar>
        </Box>
    );
};

export default GetProductTabs;
